import React, { useContext, useReducer } from 'react';

import {
  GetForm_vm_form_formItems_VOLUNTEER_FormItemFieldType as FieldFormItemType,
  GetForm_vm_form_formItems_VOLUNTEER_FormItemContentType as ContentFormItemType,
  GetForm_vm_form_formItems_VOLUNTEER_FormItemHeadingType as HeadingFormItemType,
  GetForm_vm_form_formItems_VOLUNTEER_FormItemMediaType as MediaFormItemType,
} from '../__generated__/GetForm';

export interface RepeatableGroupFormItemData {
  __typename: 'VOLUNTEER_FormItemRepeatableGroupType';
  formItemId: string;
  order: number;
  title: string | null;
  description: string | null;
  label: string | null;
  minInstances: number;
  maxInstances: number;
  groupItems: Array<{ __typename: string; formItemId: string; order: number; [key: string]: any }>;
}

type DialogState = {
  open: boolean;
};

interface HeadingDialogState extends DialogState {
  data: HeadingFormItemType | null;
}

interface ContentDialogState extends DialogState {
  data: ContentFormItemType | null;
}

interface FieldDialogState extends DialogState {
  data: FieldFormItemType | null;
  targetGroupId?: string;
}

interface MediaDialogState extends DialogState {
  data: MediaFormItemType | null;
}

interface RepeatableGroupDialogState extends DialogState {
  data: RepeatableGroupFormItemData | null;
}

interface FormBuilderDialogContextInterface {
  openHeadingDialogState: HeadingDialogState;
  addHeading: () => void;
  editHeading: (x: HeadingFormItemType) => void;
  closeHeadingDialog: () => void;
  openContentDialogState: ContentDialogState;
  addContent: () => void;
  editContent: (x: ContentFormItemType) => void;
  closeContentDialog: () => void;
  openFieldDialogState: FieldDialogState;
  addField: (targetGroupId?: string) => void;
  editField: (x: FieldFormItemType) => void;
  closeFieldDialog: () => void;
  openMediaDialogState: MediaDialogState;
  addMedia: () => void;
  editMedia: (x: MediaFormItemType) => void;
  closeMediaDialog: () => void;
  openRepeatableGroupDialogState: RepeatableGroupDialogState;
  addRepeatableGroup: () => void;
  editRepeatableGroup: (x: RepeatableGroupFormItemData) => void;
  closeRepeatableGroupDialog: () => void;
}

interface FormBuilderDialogProviderProps {
  children: React.ReactNode;
}

type HeadingAction = { type: 'add' } | { type: 'edit'; data: HeadingFormItemType } | { type: 'close' };
type ContentAction = { type: 'add' } | { type: 'edit'; data: ContentFormItemType } | { type: 'close' };
type FieldAction =
  | { type: 'add'; targetGroupId?: string }
  | { type: 'edit'; data: FieldFormItemType }
  | { type: 'close' };
type MediaAction = { type: 'add' } | { type: 'edit'; data: MediaFormItemType } | { type: 'close' };
type RepeatableGroupAction = { type: 'add' } | { type: 'edit'; data: RepeatableGroupFormItemData } | { type: 'close' };

const headingDialogReducer = (state: HeadingDialogState, action: HeadingAction): HeadingDialogState => {
  switch (action.type) {
    case 'add':
      return { open: true, data: null };
    case 'edit':
      return { open: true, data: action.data };
    case 'close':
      return { open: false, data: null };
    default:
      return state;
  }
};

const ContentDialogReducer = (state: ContentDialogState, action: ContentAction): ContentDialogState => {
  switch (action.type) {
    case 'add':
      return { open: true, data: null };
    case 'edit':
      return { open: true, data: action.data };
    case 'close':
      return { open: false, data: null };
    default:
      return state;
  }
};

const fieldDialogReducer = (state: FieldDialogState, action: FieldAction): FieldDialogState => {
  switch (action.type) {
    case 'add':
      return { open: true, data: null, targetGroupId: action.targetGroupId };
    case 'edit':
      return { open: true, data: action.data };
    case 'close':
      return { open: false, data: null, targetGroupId: undefined };
    default:
      return state;
  }
};

const mediaDialogReducer = (state: MediaDialogState, action: MediaAction): MediaDialogState => {
  switch (action.type) {
    case 'add':
      return { open: true, data: null };
    case 'edit':
      return { open: true, data: action.data };
    case 'close':
      return { open: false, data: null };
    default:
      return state;
  }
};

const repeatableGroupDialogReducer = (
  state: RepeatableGroupDialogState,
  action: RepeatableGroupAction
): RepeatableGroupDialogState => {
  switch (action.type) {
    case 'add':
      return { open: true, data: null };
    case 'edit':
      return { open: true, data: action.data };
    case 'close':
      return { open: false, data: null };
    default:
      return state;
  }
};

const FormBuilderDialogContext = React.createContext<FormBuilderDialogContextInterface | undefined>(undefined);

export const FormBuilderDialogProvider = (props: FormBuilderDialogProviderProps) => {
  const [openHeadingDialogState, dispatchHeadingDialog] = useReducer(headingDialogReducer, { open: false, data: null });
  const [openContentDialogState, dispatchContentDialog] = useReducer(ContentDialogReducer, {
    open: false,
    data: null,
  });
  const [openFieldDialogState, dispatchFieldDialog] = useReducer(fieldDialogReducer, { open: false, data: null });
  const [openMediaDialogState, dispatchMediaDialog] = useReducer(mediaDialogReducer, { open: false, data: null });
  const [openRepeatableGroupDialogState, dispatchRepeatableGroupDialog] = useReducer(repeatableGroupDialogReducer, {
    open: false,
    data: null,
  });

  const addHeading = () => dispatchHeadingDialog({ type: 'add' });
  const editHeading = (headingData: HeadingFormItemType) =>
    dispatchHeadingDialog({
      type: 'edit',
      data: headingData,
    });
  const closeHeadingDialog = () => dispatchHeadingDialog({ type: 'close' });

  const addContent = () => dispatchContentDialog({ type: 'add' });
  const editContent = (contentData: ContentFormItemType) =>
    dispatchContentDialog({
      type: 'edit',
      data: contentData,
    });
  const closeContentDialog = () => dispatchContentDialog({ type: 'close' });

  const addField = (targetGroupId?: string) => dispatchFieldDialog({ type: 'add', targetGroupId });
  const editField = (fieldData: FieldFormItemType) => dispatchFieldDialog({ type: 'edit', data: fieldData });
  const closeFieldDialog = () => dispatchFieldDialog({ type: 'close' });

  const addMedia = () => dispatchMediaDialog({ type: 'add' });
  const editMedia = (fieldData: MediaFormItemType) => dispatchMediaDialog({ type: 'edit', data: fieldData });
  const closeMediaDialog = () => dispatchMediaDialog({ type: 'close' });

  const addRepeatableGroup = () => dispatchRepeatableGroupDialog({ type: 'add' });
  const editRepeatableGroup = (groupData: RepeatableGroupFormItemData) =>
    dispatchRepeatableGroupDialog({ type: 'edit', data: groupData });
  const closeRepeatableGroupDialog = () => dispatchRepeatableGroupDialog({ type: 'close' });

  const value = {
    openHeadingDialogState,
    addHeading,
    editHeading,
    closeHeadingDialog,
    openContentDialogState,
    addContent,
    editContent,
    closeContentDialog,
    openFieldDialogState,
    addField,
    editField,
    closeFieldDialog,
    openMediaDialogState,
    addMedia,
    editMedia,
    closeMediaDialog,
    openRepeatableGroupDialogState,
    addRepeatableGroup,
    editRepeatableGroup,
    closeRepeatableGroupDialog,
  };

  return <FormBuilderDialogContext.Provider value={value} {...props} />;
};

export const useFormBuilderDialogContext = () => {
  const value = useContext(FormBuilderDialogContext);
  if (!value) throw new Error('useFormBuilderDialogContext must be inside of a FormBuilderDialogProvider');
  return value;
};
